import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import Header from "../components/layout/Header";
import BottomNavBar from "../components/layout/BottomNav";
import { TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import {
  getAllPerformanceLogs,
  getAllPerformanceTrainingLogs,
  deletePerformanceLog,
  removeLogIDFromDayPost,
  updatePerformanceLog,
  generateDateID,
} from "../services/dayPostService";
import { useDayPostSubmission } from "../hooks/useDayPostData";
import { PerformanceLog, PerformanceType, TrainingLog } from "../types/post";
import {
  AddPerformanceLogForm,
  DeleteConfirmDialog,
} from "../components/DayPost";
import PerformanceFlow from "../components/DayPost/performance/PerformanceFlow";
import DeleteTrainingLogDialog from "../components/DayPost/training-log/DeleteTrainingLogDialog";
import { useUserEvents } from "../hooks/useUserEvents";
import { linkPerformanceToEvent } from "../services/performanceService";
import LinkToEventDialog from "../components/DayPost/performance/LinkToEventDialog";
import PeakTextField from "../components/onboarding/PeakTextField";
import {
  LIST_ROW_CLASS,
  ListAddButton,
  ListFilterToggle,
  ListPagination,
  PeakSelect,
} from "../components/common/ListControls";
import { resultValue, ResultValue } from "../utils/resultValue";

type PerfRow = PerformanceLog & { id: string };
type PerfTrainingLog = TrainingLog & { id: string };
type Intent = "all" | PerformanceType;

const INTENTS: { value: Intent; label: string }[] = [
  { value: "all", label: "All" },
  { value: "competition", label: "Competition" },
  { value: "gameDay", label: "Game Day" },
  { value: "testing", label: "Testing" },
];

const INTENT_LABEL: Record<PerformanceType, string> = {
  competition: "Competition",
  gameDay: "Game Day",
  testing: "Testing",
};

const PAGE_SIZE = 10;
const ALL = "__all__";
const RECORD_TAGS = new Set(["PB", "SB"]);

const formatDateID = (id?: string): string => {
  if (!id || id.length < 8) return "—";
  const y = parseInt(id.substring(0, 4), 10);
  const m = parseInt(id.substring(4, 6), 10);
  const d = parseInt(id.substring(6, 8), 10);
  if (!y || !m || !d) return "—";
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getActivityName = (log: PerfRow): string =>
  log.activity?.activityName ||
  log.performanceTitle ||
  log.performanceResult ||
  "Untitled";

const getPerfDate = (log: PerfRow): string =>
  log.performanceDate || log.dateID || "";

// A log meets the "new" standard when it carries a structured activity
// payload. Older free-text logs are shown read-only because the edit form
// cannot represent them losslessly.
const isLegacyLog = (log: PerfRow): boolean => !log.activity;

/** First visible measured variable of a result, as the row's headline value. */
const primaryValue = (log: PerfRow): ResultValue | null => {
  const variable = (log.activity?.variables ?? []).find(
    (v) => !v.hidden && v.measurement != null,
  );
  return variable ? resultValue(variable) : null;
};

/**
 * One row of the list, mirroring the native apps: a result (with its parent
 * performance when it has one), or a performance with no results yet.
 */
type Row = {
  key: string;
  date: string;
  timestamp: number;
  performance?: PerfTrainingLog;
  result?: PerfRow;
};

const Performances: React.FC = () => {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid;
  const [logs, setLogs] = useState<PerfRow[]>([]);
  const [trainingLogs, setTrainingLogs] = useState<PerfTrainingLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [intent, setIntent] = useState<Intent>("all");
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [activityFilter, setActivityFilter] = useState(ALL);
  const [tagFilter, setTagFilter] = useState(ALL);
  const [page, setPage] = useState(1);
  const [menu, setMenu] = useState<{ row: Row; x: number; y: number } | null>(null);
  const { events } = useUserEvents(uid);
  const [linkingLog, setLinkingLog] = useState<PerfTrainingLog | null>(null);

  const [editing, setEditing] = useState<PerfRow | null>(null);
  const [editingPerformance, setEditingPerformance] =
    useState<PerfTrainingLog | null>(null);
  const [viewing, setViewing] = useState<PerfRow | null>(null);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<PerfRow | null>(null);
  const [deletingPerformance, setDeletingPerformance] = useState<{
    log: PerfTrainingLog;
    resultCount: number;
  } | null>(null);
  const [actionInFlight, setActionInFlight] = useState(false);

  const { deleteTrainingLogById } = useDayPostSubmission();

  const fetchLogs = useCallback(async () => {
    if (!uid) return;
    setLoading(true);
    try {
      const [all, perf] = await Promise.all([
        getAllPerformanceLogs(uid),
        getAllPerformanceTrainingLogs(uid),
      ]);
      setLogs(all);
      setTrainingLogs(perf);
    } catch (err) {
      console.error("Failed to load performance logs:", err);
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const rows = useMemo<Row[]>(() => {
    const perfById = new Map(trainingLogs.map((l) => [l.id, l]));
    const withResults = new Set<string>();
    const list: Row[] = logs.map((log) => {
      const performance = log.linkedTrainingLogID
        ? perfById.get(log.linkedTrainingLogID)
        : undefined;
      if (performance) withResults.add(performance.id);
      return {
        key: log.id,
        date: performance?.dateID || getPerfDate(log),
        timestamp: log.timestamp ?? 0,
        performance,
        result: log,
      };
    });
    trainingLogs.forEach((log) => {
      if (withResults.has(log.id)) return;
      list.push({ key: `perf_${log.id}`, date: log.dateID, timestamp: log.timestamp ?? 0, performance: log });
    });
    return list.sort(
      (a, b) => b.date.localeCompare(a.date) || a.timestamp - b.timestamp,
    );
  }, [logs, trainingLogs]);

  const activityOptions = useMemo(
    () =>
      Array.from(
        new Set(rows.filter((r) => r.result).map((r) => getActivityName(r.result!))),
      ).sort((a, b) => a.localeCompare(b)),
    [rows],
  );

  const tagOptions = useMemo(
    () =>
      Array.from(new Set(rows.flatMap((r) => r.result?.tag ?? []))).sort((a, b) =>
        a.localeCompare(b),
      ),
    [rows],
  );

  const eventName = useCallback(
    (row: Row) => {
      const id = row.performance?.eventId;
      return id ? events.find((e) => e.id === id)?.name : undefined;
    },
    [events],
  );

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (intent !== "all" && row.performance?.performanceType !== intent) return false;
      if (activityFilter !== ALL && (!row.result || getActivityName(row.result) !== activityFilter)) return false;
      if (tagFilter !== ALL && !(row.result?.tag ?? []).includes(tagFilter)) return false;
      if (!query) return true;
      const haystack = [
        row.result ? getActivityName(row.result) : "",
        eventName(row) ?? "",
        ...(row.result?.tag ?? []),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [rows, intent, activityFilter, tagFilter, search, eventName]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [intent, activityFilter, tagFilter, search]);

  const filtersActive = activityFilter !== ALL || tagFilter !== ALL;

  const openRow = (row: Row) => {
    if (row.result) {
      if (isLegacyLog(row.result)) setViewing(row.result);
      else if (row.performance) setEditingPerformance(row.performance);
      else setEditing(row.result);
    } else if (row.performance) {
      setEditingPerformance(row.performance);
    }
  };

  const deleteRow = (row: Row) => {
    if (row.result) setDeleting(row.result);
    else if (row.performance) setDeletingPerformance({ log: row.performance, resultCount: 0 });
  };

  const handleEditSubmit = async (data: any) => {
    if (!uid || !editing) return "";
    setActionInFlight(true);
    try {
      await updatePerformanceLog(uid, editing.id, data);
      setEditing(null);
      await fetchLogs();
    } catch (err) {
      console.error("Failed to update performance log:", err);
    } finally {
      setActionInFlight(false);
    }
    return "";
  };

  const handleUnlink = async (log: PerfTrainingLog) => {
    if (!uid) return;
    setActionInFlight(true);
    try {
      await linkPerformanceToEvent(uid, log.id, null);
      await fetchLogs();
    } catch (err) {
      console.error("Failed to unlink performance:", err);
    } finally {
      setActionInFlight(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!uid || !deleting) return;
    setActionInFlight(true);
    try {
      await deletePerformanceLog(uid, deleting.id);
      if (deleting.dateID) {
        try {
          await removeLogIDFromDayPost(
            uid,
            deleting.dateID,
            deleting.id,
            "performanceLogIDs",
          );
        } catch (err) {
          console.warn("Failed to remove log ID from day post:", err);
        }
      }
      if (editing?.id === deleting.id) setEditing(null);
      setDeleting(null);
      await fetchLogs();
    } catch (err) {
      console.error("Failed to delete performance log:", err);
    } finally {
      setActionInFlight(false);
    }
  };

  const handleConfirmDeletePerformance = async (
    deleteLinkedSessionsAndActivity: boolean,
    deleteLinkedResults: boolean = false,
  ) => {
    if (!uid || !deletingPerformance) return;
    setActionInFlight(true);
    try {
      await deleteTrainingLogById(
        deletingPerformance.log.id,
        deletingPerformance.log.dateID,
        deleteLinkedSessionsAndActivity,
        undefined,
        deleteLinkedResults,
      );
      setDeletingPerformance(null);
      await fetchLogs();
    } catch (err) {
      console.error("Failed to delete performance:", err);
    } finally {
      setActionInFlight(false);
    }
  };

  const menuActions = (row: Row): { label: string; destructive?: boolean; run: () => void }[] => {
    const actions: { label: string; destructive?: boolean; run: () => void }[] = [];
    const legacy = row.result && isLegacyLog(row.result);
    actions.push({ label: legacy ? "View" : "Edit", run: () => openRow(row) });
    if (row.performance) {
      const perf = row.performance;
      actions.push({
        label: perf.eventId ? "Change event" : "Link to event",
        run: () => setLinkingLog(perf),
      });
      if (perf.eventId) actions.push({ label: "Unlink event", run: () => handleUnlink(perf) });
    }
    actions.push({ label: "Delete", destructive: true, run: () => deleteRow(row) });
    return actions;
  };

  return (
    <div className="min-h-screen">
      <Header />

      <div className="pt-16 pb-32">
        <div className="max-w-5xl mx-auto">
          <div className="flex justify-between items-center mb-6 pt-8 px-8 sm:px-0">
            <h1 className="text-3xl sm:text-3xl md:text-4xl font-bold text-base-content font-heading">
              Performances
            </h1>
            <ListAddButton label="Add performance" onClick={() => setAdding(true)} />
          </div>

          <div className="px-4 sm:px-0">
            <div className="mb-6 flex items-start gap-3 rounded-lg bg-peak-bg-secondary p-4">
              <span className="mt-0.5 inline-flex items-center rounded-full border border-peak-border px-2 py-0.5 font-funnel text-[11px] font-semibold text-peak-text-secondary">
                Beta
              </span>
              <p className="font-funnel text-sm text-peak-text-secondary">
                This page is in beta and updates are currently being worked on.
                Some features may change or be temporarily unavailable.
              </p>
            </div>

            <div className="mb-4 space-y-3">
              <PeakSelect
                label="Type"
                value={intent}
                options={INTENTS}
                onChange={(v) => setIntent(v as Intent)}
                emphasizedValue
              />
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <PeakTextField
                    label="Search"
                    value={search}
                    onChange={setSearch}
                    placeholder="Search performances…"
                  />
                </div>
                <ListFilterToggle
                  open={showFilters}
                  active={showFilters || filtersActive}
                  onToggle={() => setShowFilters((s) => !s)}
                />
              </div>
              {showFilters && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <PeakSelect
                    label="Activity"
                    value={activityFilter}
                    options={[
                      { value: ALL, label: "All activities" },
                      ...activityOptions.map((a) => ({ value: a, label: a })),
                    ]}
                    onChange={setActivityFilter}
                    emphasizedValue
                  />
                  <PeakSelect
                    label="Tag"
                    value={tagFilter}
                    options={[
                      { value: ALL, label: "All tags" },
                      ...tagOptions.map((t) => ({ value: t, label: t })),
                    ]}
                    onChange={setTagFilter}
                    emphasizedValue
                  />
                </div>
              )}
            </div>

            {loading ? (
              <div className="text-center py-16 font-funnel text-sm text-peak-text-tertiary">
                Loading performances...
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16 font-funnel text-sm text-peak-text-tertiary">
                {rows.length === 0
                  ? "No performances logged yet."
                  : "No performances match these filters."}
              </div>
            ) : (
              <>
                <div className="border-t border-peak-border">
                  {pageRows.map((row) => (
                    <SwipeToDeleteRow
                      key={row.key}
                      onTap={() => openRow(row)}
                      onDelete={() => deleteRow(row)}
                      onMenu={(x, y) => setMenu({ row, x, y })}
                    >
                      <PerformanceRowContent row={row} eventName={eventName(row)} />
                    </SwipeToDeleteRow>
                  ))}
                </div>
                <ListPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setPage}
                />
              </>
            )}
          </div>
        </div>
      </div>

      {menu && (
        <RowMenu
          x={menu.x}
          y={menu.y}
          actions={menuActions(menu.row)}
          disabled={actionInFlight}
          onClose={() => setMenu(null)}
        />
      )}

      {/* Add → the five-step Performance flow (PerformanceLog.md §3). */}
      <PerformanceFlow
        open={adding}
        onClose={() => setAdding(false)}
        dateID={generateDateID(new Date())}
        onSaved={async () => {
          await fetchLogs();
        }}
      />

      {/* Edit a performance (training log + results) */}
      <PerformanceFlow
        open={!!editingPerformance}
        onClose={() => setEditingPerformance(null)}
        dateID={editingPerformance?.dateID ?? generateDateID(new Date())}
        editMode
        initialTrainingLog={editingPerformance ?? undefined}
        onSaved={async () => {
          await fetchLogs();
        }}
      />

      {editing && (
        <AddPerformanceLogForm
          open={!!editing}
          onClose={() => setEditing(null)}
          onSubmit={handleEditSubmit}
          dateID={editing.dateID}
          editMode
          initialData={{
            notes: editing.notes,
            performanceExpectation: editing.performanceExpectation,
            performanceResult: editing.performanceResult,
            performanceTitle: editing.performanceTitle,
            performanceDate: editing.performanceDate,
            activity: editing.activity,
            linkedSessionID: editing.linkedSessionID,
            linkedTrainingLogID: editing.linkedTrainingLogID,
            tag: editing.tag,
          }}
          logId={editing.id}
          onDelete={() => setDeleting(editing)}
        />
      )}

      {viewing && (
        <LegacyPerformanceViewer log={viewing} onClose={() => setViewing(null)} />
      )}

      {uid && linkingLog && (
        <LinkToEventDialog
          isOpen={!!linkingLog}
          onClose={() => setLinkingLog(null)}
          userId={uid}
          trainingLogId={linkingLog.id}
          dateID={linkingLog.dateID}
          onLinked={() => {
            fetchLogs();
          }}
        />
      )}

      <DeleteConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleConfirmDelete}
        logType="performance"
        isDeleting={actionInFlight}
      />

      <DeleteTrainingLogDialog
        open={!!deletingPerformance}
        log={deletingPerformance?.log ?? null}
        linkedResultCount={deletingPerformance?.resultCount ?? 0}
        isDeleting={actionInFlight}
        onCancel={() => setDeletingPerformance(null)}
        onConfirm={handleConfirmDeletePerformance}
      />

      <BottomNavBar />
    </div>
  );
};

const TableTag: React.FC<{ label: string }> = ({ label }) => (
  <span className="rounded-full bg-peak-bg-secondary px-[7px] py-0.5 font-funnel text-[11px] font-semibold leading-4 text-peak-text-secondary">
    {label}
  </span>
);

/** PB / SB: gold outline beside the result title. */
const RecordTag: React.FC<{ label: string }> = ({ label }) => (
  <span className="flex-shrink-0 rounded-full border border-peak-accent-performance px-1.5 py-px font-geist text-[10px] font-semibold leading-4 text-peak-accent-performance">
    {label}
  </span>
);

const PerformanceRowContent: React.FC<{ row: Row; eventName?: string }> = ({
  row,
  eventName,
}) => {
  const { result, performance } = row;
  const tags = (result?.tag ?? []).filter((t) => !RECORD_TAGS.has(t));
  const records = (result?.tag ?? []).filter((t) => RECORD_TAGS.has(t));
  const value = result ? primaryValue(result) : null;
  const eventDay = eventName && performance?.eventDay ? `Day ${performance.eventDay}` : undefined;
  const title = result
    ? getActivityName(result)
    : eventName || (performance?.performanceType ? INTENT_LABEL[performance.performanceType] : "Performance");

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-1.5 truncate font-funnel text-xs font-medium text-peak-text-tertiary">
          <span className="font-geist">{formatDateID(row.date)}</span>
          {result && eventName && <span className="truncate">·&nbsp; {eventName}</span>}
          {eventDay && <span className="flex-shrink-0">·&nbsp; {eventDay}</span>}
        </div>
        <div className="flex flex-shrink-0 items-center gap-1">
          {performance?.performanceType && (
            <TableTag label={INTENT_LABEL[performance.performanceType]} />
          )}
          {tags.map((t) => (
            <TableTag key={t} label={t} />
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="truncate font-funnel text-[15px] font-semibold text-peak-text">
            {title}
          </span>
          {records.map((t) => (
            <RecordTag key={t} label={t} />
          ))}
        </div>
        <div className="flex-shrink-0 text-right">
          {value ? (
            <span className="whitespace-nowrap">
              <span className="font-geist text-[17px] font-semibold tabular-nums text-peak-text">
                {value.number}
              </span>
              {value.unit && (
                <span className="ml-[3px] font-funnel text-xs text-peak-text-tertiary">
                  {value.unit}
                </span>
              )}
            </span>
          ) : result?.performanceResult ? (
            <span className="font-funnel text-[15px] font-semibold text-peak-text">
              {result.performanceResult}
            </span>
          ) : (
            <span className="font-funnel text-xs text-peak-text-tertiary">
              {result ? "—" : "No results"}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const DELETE_WIDTH = 84;
const LONG_PRESS_MS = 500;

/**
 * Swipe right to reveal Delete (touch or mouse drag). Tap opens the row;
 * long-press or right-click opens the row menu.
 */
const SwipeToDeleteRow: React.FC<{
  onTap: () => void;
  onDelete: () => void;
  onMenu: (x: number, y: number) => void;
  children: React.ReactNode;
}> = ({ onTap, onDelete, onMenu, children }) => {
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const gesture = useRef<{ x: number; y: number; base: number; axis: "x" | "y" | null } | null>(null);
  const longPress = useRef<number | undefined>(undefined);
  const suppressClick = useRef(false);

  const clearLongPress = () => window.clearTimeout(longPress.current);

  useEffect(() => clearLongPress, []);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    gesture.current = { x: e.clientX, y: e.clientY, base: offset, axis: null };
    if (e.pointerType !== "mouse") {
      const { clientX, clientY } = e;
      longPress.current = window.setTimeout(() => {
        gesture.current = null;
        suppressClick.current = true;
        onMenu(clientX, clientY);
      }, LONG_PRESS_MS);
    }
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const g = gesture.current;
    if (!g) return;
    const dx = e.clientX - g.x;
    const dy = e.clientY - g.y;
    if (Math.abs(dx) > 8 || Math.abs(dy) > 8) clearLongPress();
    if (g.axis === null) {
      if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
        g.axis = "x";
        setDragging(true);
        e.currentTarget.setPointerCapture(e.pointerId);
      } else if (Math.abs(dy) > 10) {
        g.axis = "y";
      }
    }
    if (g.axis === "x") setOffset(Math.min(DELETE_WIDTH, Math.max(0, g.base + dx)));
  };

  const onPointerEnd = () => {
    clearLongPress();
    const g = gesture.current;
    gesture.current = null;
    if (g?.axis === "x") {
      suppressClick.current = true;
      setDragging(false);
      setOffset((o) => (o > DELETE_WIDTH / 2 ? DELETE_WIDTH : 0));
    }
  };

  const onClick = () => {
    if (suppressClick.current) {
      suppressClick.current = false;
      return;
    }
    if (offset > 0) setOffset(0);
    else onTap();
  };

  return (
    <div className="relative overflow-hidden">
      {offset > 0 && (
        <div className="absolute inset-y-0 left-0 flex items-center px-1.5" style={{ width: DELETE_WIDTH }}>
          <button
            type="button"
            onClick={() => {
              setOffset(0);
              onDelete();
            }}
            className="flex h-[calc(100%-12px)] w-full flex-col items-center justify-center gap-0.5 rounded-[14px] bg-peak-error text-white"
          >
            <TrashIcon className="h-5 w-5" />
            <span className="font-funnel text-[11px] font-semibold">Delete</span>
          </button>
        </div>
      )}
      <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onTap();
          }
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          clearLongPress();
          onMenu(e.clientX, e.clientY);
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
        className={`${LIST_ROW_CLASS} relative cursor-pointer select-none touch-pan-y hover:bg-peak-bg-secondary/50 ${
          dragging ? "" : "transition-transform duration-200 ease-out"
        }`}
        style={{ transform: `translateX(${offset}px)` }}
      >
        {children}
      </div>
    </div>
  );
};

/** Contextual menu anchored at the press / click point. */
const RowMenu: React.FC<{
  x: number;
  y: number;
  actions: { label: string; destructive?: boolean; run: () => void }[];
  disabled?: boolean;
  onClose: () => void;
}> = ({ x, y, actions, disabled, onClose }) => {
  const width = 200;
  const left = Math.min(x, window.innerWidth - width - 8);
  const top = Math.min(y, window.innerHeight - actions.length * 44 - 16);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[10000]" onClick={onClose} onContextMenu={(e) => { e.preventDefault(); onClose(); }}>
      <div
        role="menu"
        className="absolute overflow-hidden rounded-xl border border-peak-border bg-base-100 py-1 shadow-lg"
        style={{ left, top, width }}
        onClick={(e) => e.stopPropagation()}
      >
        {actions.map((a) => (
          <button
            key={a.label}
            type="button"
            role="menuitem"
            disabled={disabled}
            onClick={() => {
              onClose();
              a.run();
            }}
            className={`block w-full px-4 py-2.5 text-left font-funnel text-[15px] hover:bg-peak-bg-secondary disabled:opacity-50 ${
              a.destructive ? "text-peak-error" : "text-peak-text"
            }`}
          >
            {a.label}
          </button>
        ))}
      </div>
    </div>
  );
};

const LegacyPerformanceViewer: React.FC<{
  log: PerfRow;
  onClose: () => void;
}> = ({ log, onClose }) => {
  const rows: { label: string; value: React.ReactNode }[] = [
    { label: "Performance date", value: formatDateID(getPerfDate(log)) },
    { label: "Logged on", value: formatDateID(log.dateID) },
    log.performanceTitle && {
      label: "Title",
      value: log.performanceTitle,
    },
    log.performanceResult && {
      label: "Result",
      value: log.performanceResult,
    },
    typeof log.performanceExpectation === "number" && {
      label: "Rating",
      value: `${log.performanceExpectation}/10`,
    },
    log.tag && log.tag.length > 0 && {
      label: "Tags",
      value: log.tag.join(", "),
    },
    log.linkedSessionID && {
      label: "Linked session ID",
      value: (
        <span className="font-mono text-xs break-all">
          {log.linkedSessionID}
        </span>
      ),
    },
    log.linkedTrainingLogID && {
      label: "Linked training log ID",
      value: (
        <span className="font-mono text-xs break-all">
          {log.linkedTrainingLogID}
        </span>
      ),
    },
    log.notes && { label: "Notes", value: log.notes },
  ].filter(Boolean) as { label: string; value: React.ReactNode }[];

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur">
      <div className="bg-base-100 rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 pb-2">
          <h2 className="text-xl font-sans font-semibold text-base-content">
            Performance log
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-base-200"
            aria-label="Close"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 pb-2">
          <p className="text-xs text-base-content/60">
            This log was saved in an older format and is shown read-only.
          </p>
        </div>
        <div className="px-6 py-4 space-y-3">
          {rows.map((r) => (
            <div key={r.label}>
              <div className="text-[11px] font-semibold tracking-wide uppercase text-base-content/60">
                {r.label}
              </div>
              <div className="text-sm text-base-content mt-0.5 whitespace-pre-wrap break-words">
                {r.value}
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-end p-6 pt-2">
          <button
            onClick={onClose}
            className="bg-base-200 px-3 py-2 rounded-md font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default Performances;
